import { httpClient } from '../http-client'
import {
    checkToken,
    excelDateToJSDate,
    handleError,
    wiseOptionsRateLimiter,
} from '../utils'
import { demoTickers } from '../constants'

/**
 * Get live and historical stock options data.
 * @customfunction
 * @helpurl https://blog.wisesheets.io/the-ultimate-guide-to-using-wisesheets/
 * @param {string[][]} symbols_or_contract_id Company symbols or contract ID.
 * @param {string[][]} type Put or Call.
 * @param {string[][]} parameters Data point(s) to retrieve.
 * @param {string[][]} [date] Date of contract(s) (optional).
 * @returns {any[][]} Data or some error.
 */
// @ts-ignore
async function WISEOPTIONS(symbols_or_contract_id, type, parameters, date) {
    if (symbols_or_contract_id.flat().length > 5001) {
        return [['Please select less than 5,000 tickers to get data.']]
    }
    // html encoding for ^ maintaining original structure including empty symbols
    const filteredSymbols = symbols_or_contract_id.map((row: string[]) =>
        row.map((symbol: string) =>
            symbol.toUpperCase().replace('^', '%5E').trim()
        )
    )

    const symbolsToCheck = filteredSymbols
        .flat()
        .filter((symbol: string) => symbol !== '')

    if (symbolsToCheck.length === 0) {
        return [['Please enter at least one valid symbol.']]
    }

    // Check if user is subscribed
    const { error, subscriptionType } = checkToken(symbolsToCheck, demoTickers)
    if (error) {
        return error
    }

    try {
        // Batch requests in groups of 1000 tickers using filtered symbols
        const BATCH_SIZE = 1000

        // Calculate weight (number of batches) for rate limiting
        const weight = Math.ceil(symbolsToCheck.length / BATCH_SIZE)

        const rateLimitError = await wiseOptionsRateLimiter.checkAndApplyLimit(
            subscriptionType,
            weight
        )
        if (rateLimitError) {
            return [[rateLimitError]]
        }

        // If symbols length is within batch size, make single request
        if (filteredSymbols.flat().length <= BATCH_SIZE) {
            const data = await httpClient.post(
                '/api/private/functions/wise-options',
                {
                    symbols: filteredSymbols,
                    type,
                    parameters,
                    date:
                        date == null ? null : [[excelDateToJSDate(date[0][0])]],
                    platform: 'EXCEL',
                }
            )
            return data.data
        }

        // Create batches from filteredSymbols maintaining 2D array structure
        const batches: string[][][] = []
        let currentBatch: string[][] = []
        let currentCount = 0

        for (const row of filteredSymbols) {
            for (const symbol of row) {
                if (currentCount >= BATCH_SIZE) {
                    batches.push(currentBatch)
                    currentBatch = []
                    currentCount = 0
                }
                currentBatch.push([symbol])
                currentCount++
            }
        }

        // Push the last batch if it has items
        if (currentBatch.length > 0) {
            batches.push(currentBatch)
        }

        // Process all batches in parallel, skipping all-empty batches
        const batchPromises = batches.map((batch) => {
            if (batch.flat().every((s: string) => s === '')) {
                return Promise.resolve(null)
            }
            return httpClient.post('/api/private/functions/wise-options', {
                symbols: batch,
                type,
                parameters,
                date: date == null ? null : [[excelDateToJSDate(date[0][0])]],
                platform: 'EXCEL',
            })
        })

        // Wait for all requests to complete (order is maintained)
        const batchResponses = await Promise.all(batchPromises)

        // Combine results from all batches
        const results: any[][] = []
        for (const response of batchResponses) {
            if (response === null) continue
            // Each batch returns a 2D array, concatenate rows
            for (const row of response.data) {
                results.push(row)
            }
        }

        return results
    } catch (e) {
        return handleError(e)
    }
}

// Export for FMP wrapper functions
export { WISEOPTIONS }

CustomFunctions.associate("WISEOPTIONS", WISEOPTIONS);